import { FC, useMemo, useRef } from 'react';
import { Form, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { clientConfig } from '@/config/clientConfig';
import { AssetsForms, useI18nValues } from '@/entities/assets';
import { useAssistant } from '@/entities/assistant/hooks/useAssistant';
import { useFormRenderV2 } from '@/shared/lib/hooks/useFormRenderV2';
import { FileUploadingRef } from '@/shared/lib/hooks/useFormRenderV2/components/FileUploading';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { AssetCategory, AssetDto } from '@/shared/lib/sj-orm/models/asset.dto';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { Button } from '@/shared/uikitV2/Button';

import { BtnClose } from '../../components/BtnClose';
import { SelectOptions } from '../../components/SelectItem/types';

import { Body, Container, Footer, Header, StyledDrawer, StyledTitle } from './styled';

type CreateAssetProps = {
  category?: AssetCategory;
  onCreate?: (data: SelectOptions) => void;
  setOpen: (open: boolean) => void;
};

export const CreateAsset: FC<CreateAssetProps> = ({ category, onCreate, setOpen }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectEntities' });
  const { keyPair } = useKeyPairStore();
  const sjDb = useSJDatabase();
  const values = useI18nValues();
  const uploaderRef = useRef<FileUploadingRef>(null);
  const { delegation } = useAssistant();
  const { isAssistant } = useProfileStore();

  const form = useForm<Omit<BaseDto, 'id'>>({
    defaultValues: {},
    mode: 'onChange',
  });

  const sectionItems = useMemo(() => {
    if (!category) return {};

    const sections = AssetsForms[category];
    if (!sections?.[0]) return {};

    const firstSection = sections[0];
    const attachments = sections.find((section) => 'attachments' in section)?.attachments;

    return attachments ? { ...firstSection, attachments } : firstSection;
  }, [category]);

  const { Component } = useFormRenderV2({
    sectionItems,
    defaultValues: {
      currency: clientConfig.defaultProfileBaseCurrency,
    },
    form,
    uploaderRef,
    showRequiredOnly: true,
  });

  const onSubmit = async (data: Omit<BaseDto, 'id'>) => {
    if (!keyPair || !category) return;
    const res = uploaderRef.current ? await uploaderRef.current.upload(true) : null;

    const assetDto: AssetDto = {
      ...data,
      id: sjDb.generateId(CollectionName.ASSETS),
      category,
    };

    if (res?.success) {
      assetDto.attachments = res.filePaths;
    }

    const publicKeys = [keyPair.publicKey];

    if (delegation?.ownerPublicKey && isAssistant) {
      publicKeys.push(delegation.ownerPublicKey);
      assetDto.isCreatedByAssistant = true;
    }

    const asset = await sjDb.collections.assets!.create(assetDto, publicKeys);
    onCreate?.({ colName: CollectionName.ASSETS, id: asset!.id });
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!category) return null;

  return (
    <FormProvider {...form}>
      <Form
        control={form.control}
        onSubmit={({ data }) => onSubmit(data)}
        render={({ submit }) => (
          <StyledDrawer width={400} footer={null} closable={false} onClose={handleClose} open>
            <Container>
              <Header>
                <BtnClose
                  onClick={handleClose}
                  style={{ position: 'absolute', right: 24, top: 24 }}
                />
                <StyledTitle className="truncate" size={22} bold>
                  {t('titleAddAsset')} - {values.category(category)}
                </StyledTitle>
              </Header>
              <Body>{Component}</Body>
              <Footer>
                <Button type="text" onClick={handleClose}>
                  {t('btnCancel')}
                </Button>
                <Button onClick={submit}>{t('btnSave')}</Button>
              </Footer>
            </Container>
          </StyledDrawer>
        )}
      />
    </FormProvider>
  );
};
